// 首页 sorting / gender 字面量 → public_list 接口取值的「纯映射函数」单一事实源。
//
// 这些映射同时被两处使用，必须保持一致：
//   - 主工程：hooks/useHomeCharacterList.ts 构造 public_list 请求体时用。
//   - 预取 boot：packages/sw-prefetch/src/prefetch-boot.ts 的 HomePlugin 预热首页列表时用（esbuild 内联进 boot）。
// 抽到此处后两边强制同调一份逻辑，杜绝人工复刻漂移（曾出现 boot 与 hook 的 default 分支不一致）。
//
// 零依赖（不 import 任何模块/类型）：入参用 string 宽松接收（调用方传的本就是 localStorage
// 字面量），返回字面量联合。这样 esbuild 打进 boot、boot 独立类型检查都不牵连主工程类型图。

// 当前合法的首页 sorting 白名单——「单一事实源」。
// 此文件零依赖，故白名单定义在此（底层），由 constant/common.ts 的 SortingList 派生（上层 import 它
// 并断言为 HomeSorting[]）。boot 的 normalizeHomeSorting 与页面 SortingList 校验同读这一份，杜绝双写漂移。
// 注意方向：必须「common.ts 派生本文件」，不能反过来（本文件零 import，不能拉进 common.ts 的 HomeSorting 类型）。
export const VALID_HOME_SORTINGS = [
  'For You',
  'Weekly Picks',
  'World',
  'New Releases',
  'All-Time Faves',
  'Following',
] as const

/**
 * 把 localStorage 的 homeSortingState 归一化为合法 sorting：在白名单内原样返回，否则 'For You'。
 * boot 与页面（hooks/useHomeCharacterList.ts → SortingList，派生自本文件 VALID_HOME_SORTINGS）同调一份，
 * 杜绝「页面归一化、boot 不归一化」导致的预取失配（旧值会让 boot 预热 public_list、页面却请求 recommend_feed → miss）。
 */
export const normalizeHomeSorting = (
  sorting: string | null | undefined
): string =>
  sorting && (VALID_HOME_SORTINGS as readonly string[]).indexOf(sorting) !== -1
    ? sorting
    : 'For You'

/** sorting → public_list 取值的映射结果（仅「有明确映射」的几种）。 */
export type MappedHomeSorting =
  | 'Popular'
  | 'Featured1D'
  | 'Featured7D'
  | 'Featured'
  | 'WeeklyPicks'
  | 'New'
  | 'FollowersCharacterNew'

/**
 * sorting 字面量 → public_list 的 sorting 取值。
 * 仅返回「有明确映射」的值；无映射返回 null，由调用方决定 fallback：
 *   - hook 侧：null 则 `?? sorting`（原样透传，保留既有行为）。
 *   - boot 侧：null 则跳过列表预热（未知 sorting 不预热，退化为正常网络请求）。
 * featuredTime 仅在 sorting==='Featured' 时参与（决定 Featured1D/Featured7D/Featured）。
 */
export const mapHomeSortingToApi = (
  sorting: string,
  featuredTime?: string | null
): MappedHomeSorting | null => {
  switch (sorting) {
    case 'All-Time Faves':
      return 'Popular'
    case 'Featured':
      // featuredTime 默认 '7d'（store/home.ts homeFeaturedTimeState）。
      return featuredTime === '1d'
        ? 'Featured1D'
        : featuredTime === 'All time'
          ? 'Featured'
          : 'Featured7D'
    case 'Weekly Picks':
      return 'WeeklyPicks'
    case 'New Releases':
      return 'New'
    case 'Following':
      return 'FollowersCharacterNew'
    default:
      return null
  }
}

/** gender 字面量 → 接口 gender 取值。无对应（如 'All'）返回 undefined。 */
export const mapGenderToApi = (
  gender?: string | null
): 'female' | 'male' | 'other' | undefined => {
  switch (gender) {
    case 'Female':
      return 'female'
    case 'Male':
      return 'male'
    case 'Non-binary':
      return 'other'
    default:
      return undefined
  }
}
